import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Users, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { API } from "@/lib/api";

export default function AdminCustomers() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadUsers = useCallback(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUsers(res.data))
      .catch(() => toast.error('Chyba načtení zákazníků'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/export-csv`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'customers.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Chyba exportu'); }
  };

  const filtered = search
    ? users.filter(u => `${u.firstname} ${u.lastname} ${u.email}`.toLowerCase().includes(search.toLowerCase()))
    : users;

  return (
    <AdminLayout active="customers">
      <div className="flex items-center justify-between mb-4" data-testid="admin-customers">
        <h1 className="text-2xl font-bold text-slate-800">Zákazníci <span className="text-sm font-normal text-slate-500">({users.length})</span></h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Hledat zákazníka..." className="h-10 w-56 border border-slate-300 rounded px-4 pr-10 text-sm" data-testid="customer-search" />
            <Search className="w-4 h-4 absolute right-3 top-3 text-slate-400" />
          </div>
          <button onClick={exportCSV} className="h-10 px-4 border border-slate-300 text-slate-600 rounded text-sm font-medium hover:bg-slate-50 flex items-center gap-2" data-testid="export-customers-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div> : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Žádní zákazníci</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Jméno</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">E-mail</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Firma</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Objednávek</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Admin</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Registrace</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{u.firstname} {u.lastname}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{u.company || '–'}</td>
                  <td className="px-4 py-3 text-center"><span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{u.order_count || 0}</span></td>
                  <td className="px-4 py-3 text-center">{u.is_admin ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Admin</span> : '–'}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('cs-CZ') : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
